import { Button, Space } from '@sravni/react-design-system';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import type { TPreApprovalOfferStepData } from 'shared/types/TRankingService';

import { useUpdateDeliveryAddressMutation } from 'entities/preApprovals';

import { DELIVERY_TEXTS, DELIVERY_FORM_FIELDS } from '../constants';
import { ControlledAddress, ControlledSlotSelect } from '../fields';
import { useFieldsControl } from '../lib/useFieldsControl';
import { usePollDeliverySlots } from '../lib/usePollDeliverySlots';
import { useSlots } from '../lib/useSlots';
import type { TMeetingFormFields } from '../types';

type Props = {
  offerData: TPreApprovalOfferStepData;
  onChangeEvent?: (
    offer: TPreApprovalOfferStepData,
    formData: TMeetingFormFields,
    fieldName: keyof TMeetingFormFields,
  ) => void;
  onSubmitEvent?: (offer: TPreApprovalOfferStepData, formData: TMeetingFormFields) => void;
};

export const FormBody: FC<Props> = (props) => {
  const { offerData, onChangeEvent, onSubmitEvent } = props;
  const { preApprovalOfferId } = offerData;
  const { isLoading, data, startPolling, isError: isSlotsError, isEmptySlots } = usePollDeliverySlots(offerData);
  const [updateAddress, { isLoading: isUpdateLoading, isError: isUpdateAddressError }] =
    useUpdateDeliveryAddressMutation();
  const { formState, getValues } = useFormContext<TMeetingFormFields>();
  const { errors } = formState || {};

  const { slots, dateSlots, timeSlots } = useSlots(data?.slots || []);

  const { address: addressFormError } = errors || {};

  useFieldsControl(slots);

  const onSelect = useCallback(
    async (address: string) => {
      onChangeEvent?.(offerData, { ...getValues(), address }, DELIVERY_FORM_FIELDS.ADDRESS);

      if (!addressFormError) {
        await updateAddress({ offerId: preApprovalOfferId || '', address });
        startPolling();
      }
    },
    [onChangeEvent, offerData, getValues, addressFormError, updateAddress, preApprovalOfferId, startPolling],
  );

  const onSubmitClick = useCallback(() => {
    const { timeSlot, ...formData } = getValues();
    const timeEventValue = timeSlots?.find((s) => s.id === timeSlot)?.label || timeSlot;
    onSubmitEvent?.(offerData, { ...formData, timeSlot: timeEventValue });
  }, [getValues, offerData, onSubmitEvent, timeSlots]);

  const addressError = isUpdateAddressError || !!addressFormError ? DELIVERY_TEXTS.addressError : '';
  const slotsError = isSlotsError || isEmptySlots ? DELIVERY_TEXTS.slotsError : '';

  const hasDeliveryFormErrors = !!addressError || !!slotsError;
  const isDeliveryFormLoading = isLoading || isUpdateLoading;

  return (
    <Space
      direction="vertical"
      size={24}
    >
      <ControlledAddress
        name={DELIVERY_FORM_FIELDS.ADDRESS}
        onSelect={onSelect}
        error={addressError || slotsError}
      />
      {!hasDeliveryFormErrors && !isEmptySlots && (
        <>
          <ControlledSlotSelect
            slots={dateSlots}
            name={DELIVERY_FORM_FIELDS.DATE_SLOT}
            isLoading={isDeliveryFormLoading}
            offerData={offerData}
            onChangeEvent={onChangeEvent}
          />
          <ControlledSlotSelect
            slots={timeSlots}
            name={DELIVERY_FORM_FIELDS.TIME_SLOT}
            isLoading={isDeliveryFormLoading}
            offerData={offerData}
            onChangeEvent={onChangeEvent}
          />
        </>
      )}
      <Space justify="end">
        <Button
          type="submit"
          variant="primary"
          onClick={onSubmitClick}
          loading={isDeliveryFormLoading}
          disabled={hasDeliveryFormErrors}
        >
          {DELIVERY_TEXTS.button}
        </Button>
      </Space>
    </Space>
  );
};
